import { Component } from '@angular/core';
import { mergeMap, finalize } from 'rxjs/operators';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import {
  AbstractBlModal,
  ModalSize,
  ModalButtonType,
  ModalReason,
  BlModalService,
  BlDialogService,
} from '@blcloud/bl-ng-ui-component';
import { JapanEraType } from '@blcloud/bl-common';
import { UseDivArray, UseDiv } from '@blcloud/bl-datamodel/enum/bizcmn/use-div';
import { ApplyPeriodCriteriaDivArray, ApplyPeriodCriteriaDiv } from '@blcloud/bl-datamodel/enum/bizcmn/apply-period-criteria-div';
import { MonthWeekDayDivArray, MonthWeekDayDiv } from '@blcloud/bl-datamodel/enum/bizcmn/month-week-day-div';
import { EmployeeGuideComponent } from '@blcloud/bl-ng-share-module';
import * as moment from 'moment';
import { IContactAlertSettingEx } from './contact-alert-setting-modal.interface';
import { ContactAlertSettingModalService } from './contact-alert-setting-modal.service';
import {
  isEmpty as _isEmpty, cloneDeep as _cloneDeep, filter as _filter, isNil as _isNil, isEmpty, forEach as _forEach, find as _find,
  reject as _reject
} from 'lodash';

/**
 * コンタクトアラート設定コンポーネント
 */
@Component({
  selector: 'app-contact-alert-setting-modal',
  templateUrl: './contact-alert-setting-modal.component.html',
  styleUrls: ['./contact-alert-setting-modal.component.scss'],
  providers: [ContactAlertSettingModalService]
})
export class ContactAlertSettingModalComponent extends AbstractBlModal {

  /** 画面タイトル */
  headerTitle = 'コンタクトアラート設定';

  /** コンタクトアラート設定拡張 */
  contactAlertSettingEx: IContactAlertSettingEx = {};

  /** 実施内容ごとに選択するチェックボックス選択 */
  isSelectIndividually = false;

  /** コンタクトグループ区分チェックボックス選択 */
  isSelectContactCheckDivArray = false;

  /** コンタクトグループ区分配列 */
  contactCheckDivArray: { code: number, name: string }[] = [
    { code: 1, name: '車検' }, { code: 2, name: '点検' }, { code: 3, name: 'その他' }
  ];

  /** コンタクト実施内容配列 */
  contactImplDivArray: { code: number, name: string, div: number }[] = [];

  /** 適用期間基準区分 */
  applyPeriodCriteriaDivItems = ApplyPeriodCriteriaDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** 月週日区分 */
  monthWeekDayDivItems = MonthWeekDayDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** アラート機能使用区分 */
  useDivItems = UseDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /**
   * モーダルボタンタイプ
   * @ignore
   */
  readonly ModalButtonType = ModalButtonType;

  /**
   * 元号のタイプ
   * @ignore
   */
  readonly JapanEraType = JapanEraType;

  /** アラート機能を使用するかどうかを返します。 */
  get isAlertFunctionUse() {
    return this.contactAlertSettingEx.alertFunctionUseDiv === UseDiv.Use;
  }

  /** 適用開始期間基準が「本日」かどうかを返します。 */
  get isApplyStartPeriodToday() {
    return this.contactAlertSettingEx.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /** 適用終了期間基準が「本日」かどうかを返します。 */
  get isApplyEndPeriodToday() {
    return this.contactAlertSettingEx.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /** ログインユーザコード */
  loginUserEmployeeCode: string;

  /** ログインユーザ名 */
  loginUserEmployeeName: string;

  // --------------------------------------------------
  // Life Cycle
  /**
   * コンストラクタ
   * @param service
   */
  constructor(
    private modalService: BlModalService,
    private service: ContactAlertSettingModalService,
    private dialogService: BlDialogService,
  ) {
    super();
  }

  onShown() {
    this.showLoading();

    this.service.getLoginUserEmployeeBindModel().pipe(
      mergeMap(loginUserEmployee => {
        this.loginUserEmployeeCode = loginUserEmployee.employeeCode;
        this.loginUserEmployeeName = loginUserEmployee.employeeName;
        return ObservableForkJoin(
          this.service.getContactAlertSetting(loginUserEmployee.employeeCode),
          this.service.getContactExecuteItem(loginUserEmployee.organizationCode),
        );
      }),
      finalize(() => this.hideLoading())
    ).subscribe(
      responses => {
        this.contactAlertSettingEx = _cloneDeep(responses[0]);
        this.contactImplDivArray = _cloneDeep(responses[1]);

        this.isSelectIndividually = _isEmpty(this.contactAlertSettingEx.contactExecuteItemCodeList);
        this.isSelectContactCheckDivArray = !_isEmpty(this.contactAlertSettingEx.contactCheckDivList);
      }
    );
  }

  // --------------------------------------------------
  // AbstractModal Override
  /**
   * @override
   */
  getResults() {
    return this.contactAlertSettingEx;
  }

  /**
   * @override
   */
  defaultOption() {
    return {
      size: ModalSize.Large,
    };
  }

  /**
   * モーダル終了イベント
   * @param reason　モーダル終了理由
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      if (this.validate()) {
        this.dialogService.confirm('コンタクトアラート設定を変更します。よろしいですか？').subscribe(
          modalRef => {
            if (modalRef.reason === ModalReason.Done) {
              this.contactAlertSettingEx.employeeCode = this.loginUserEmployeeCode;
              this.contactAlertSettingEx.employeeName = this.loginUserEmployeeName;
              this.emitDismissEvent(reason);
            }
            modalRef.hide();
          }
        );
      }
    } else {
      this.emitDismissEvent(reason);
    }
  }

  // --------------------------------------------------
  // 内部処理

  /**
   * コンタクトグループ区分チェックボックス押下
   * @param event
   */
  onChangeContactCheckDivArray(event: any) {
    const checkItems = _filter(event, item => !_isNil(item));

    if (checkItems.length > 0) {
      this.isSelectContactCheckDivArray = true;
      const selectedValues = _forEach(event, selectedCheckBox => {
        return selectedCheckBox.value;
      });
      this.contactAlertSettingEx.contactCheckDivList = selectedValues;
      // 実施内容区分(チェックボックス)に対応する実施内容(マルチセレクト)を選択状態にする
      const numbers: number[] = [];
      selectedValues.forEach(value => {
        this.contactImplDivArray.forEach(contactImplDiv => {
          if (value === contactImplDiv.div) {
            numbers.push(contactImplDiv.code);
          }
        });
      });
      this.contactAlertSettingEx.contactExecuteItemCodeList = numbers;
    } else {
      this.isSelectContactCheckDivArray = false;
      this.contactAlertSettingEx.contactCheckDivList = [];
      this.contactAlertSettingEx.contactExecuteItemCodeList = [];
    }
  }

  /**
   * 実施内容ごとに選択するチェックボックス押下
   * @param event
   */
  onChangeIsSelectIndividually(event: boolean) {
    this.isSelectIndividually = event;
    if (!event) {
      this.contactAlertSettingEx.contactCheckDivList = [];
      this.contactAlertSettingEx.contactExecuteItemCodeList = [];
    }
  }

  /**
   * コンタクト実施内容セレクトボックス押下
   * @param event
   */
  onChangeContactImplDiv(event: any) {
    if (event.checked) {
      this.contactAlertSettingEx.contactExecuteItemCodeList.push(event.item.dropDownValue);
    } else {
      this.contactAlertSettingEx.contactExecuteItemCodeList =
        _filter(this.contactAlertSettingEx.contactExecuteItemCodeList,
          code => code !== event.item.dropDownValue
        );
      const findItem = _find(this.contactImplDivArray, item => item.code === event.item.dropDownValue);
      if (findItem) {
        // 実施内容のグループ区分を再設定
        this.contactAlertSettingEx.contactCheckDivList
          = _reject(this.contactAlertSettingEx.contactCheckDivList, div => div === findItem.div);
      }
    }
  }

  /**
   *  ログイン担当者を使用するチェックボックス押下
   * @param checked　チェック状態
   */
  onChangeAlertPicUseDiv(checked: boolean) {
    this.contactAlertSettingEx.alertPicUseDiv = checked;
    this.contactAlertSettingEx.alertPicCode = this.loginUserEmployeeCode;
    this.contactAlertSettingEx.alertPicName = this.loginUserEmployeeName;
  }

  /**
   * 従業員コード手入力
   * @param value　従業員コード
   */
  onChangeEmployee(value: string) {
    if (isEmpty(value)) {
      this.contactAlertSettingEx.alertPicCode = '';
      this.contactAlertSettingEx.alertPicName = '';
    } else {
      this.service.getEmployee(value).subscribe(
        emp => {
          if (!_isEmpty(emp)) {
            this.contactAlertSettingEx.alertPicCode = emp.employeeCode;
            this.contactAlertSettingEx.alertPicName = emp.employeeName;
          } else {
            this.contactAlertSettingEx.alertPicCode = '';
            this.contactAlertSettingEx.alertPicName = '';
          }
        }
      );
    }
  }

  /**
   * 担当従業員のガイド押下処理
   * @param element フォーカスエレメント
   */
  onClickEmployeeGuide(element: HTMLElement) {
    this.modalService.show(EmployeeGuideComponent).subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          const result = modalRef.getResults();
          this.contactAlertSettingEx.alertPicCode = result.employeeCode;
          this.contactAlertSettingEx.alertPicName = result.employeeName;
        }
        modalRef.hide(element);
      }
    );
  }

  /**
   * 整合性チェック処理
   */
  private validate() {
    if (!this.validateStartInputValues() || !this.validateEndInputValues()) {
      this.dialogService.warn('対象期間（実施予定日）を入力してください。').subscribe(modalRef => modalRef.hide());
    } else if (this.contactAlertSettingEx.applyStartDate === '1900-01-01') {
      this.dialogService.warn('適用開始日を入力してください。').subscribe(modalRef => modalRef.hide());
    } else {
      // 対象開始日
      const start = this.getApplyPeriodDate(
        this.contactAlertSettingEx.applyStartPeriodCriteriaDiv,
        this.contactAlertSettingEx.monthWeekDayDiv,
        this.contactAlertSettingEx.applyStartNum
      );
      // 対象終了日
      const end = this.getApplyPeriodDate(
        this.contactAlertSettingEx.applyEndPeriodCriteriaDiv,
        this.contactAlertSettingEx.monthWeekDayDiv,
        this.contactAlertSettingEx.applyEndNum
      );

      // 開始期間が終了期間よりも後の日付になっていた場合
      if (start > end) {
        this.dialogService.warn('対象期間が不正です。入力内容を確認してください。').subscribe(modalRef => modalRef.hide());
        // 期間が一年未満かチェック
      } else if (start.add(12, 'M') < end) {
        this.dialogService.warn('対象期間は1年以内で指定してください。').subscribe(modalRef => modalRef.hide());
      } else {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間（開始）入力チェック
   * applyStartPeriodCriteriaDivがTodayの場合、常にtrueを返却。
   * applyStartPeriodCriteriaDivがToday以外の場合、applyStartNumが1以上であればtrueを返却。
   */
  validateStartInputValues(): boolean {
    if (this.contactAlertSettingEx.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
      return true;
    } else {
      if (this.contactAlertSettingEx.applyStartNum) {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間（終了）入力チェック
   * applyEndPeriodCriteriaDivがTodayの場合、常にtrueを返却。
   * applyEndPeriodCriteriaDivがToday以外の場合、applyEndNumが1以上であればtrueを返却。
   */
  validateEndInputValues(): boolean {
    if (this.contactAlertSettingEx.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
      return true;
    } else {
      if (this.contactAlertSettingEx.applyEndNum) {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間日の取得処理
   * @param applyPeriodCriteriaDiv 適用期間基準区分
   * @param monthWeekDayDiv 月週日区分
   * @param applyNum 適用値
   * @returns
   */
  private getApplyPeriodDate(applyPeriodCriteriaDiv: string, monthWeekDayDiv: string, applyNum: number) {
    let date = moment().clone();

    if (applyPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.PastFromToday) {
      switch (monthWeekDayDiv) {
        case MonthWeekDayDiv.Day:
          date = date.subtract(applyNum, 'days');
          break;
        case MonthWeekDayDiv.Week:
          date = date.subtract(applyNum, 'weeks');
          break;
        case MonthWeekDayDiv.Month:
          date = date.subtract(applyNum, 'M');
          break;
      }
    } else if (applyPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.FutureFromToday) {
      switch (monthWeekDayDiv) {
        case MonthWeekDayDiv.Day:
          date = date.add(applyNum, 'days');
          break;
        case MonthWeekDayDiv.Week:
          date = date.add(applyNum, 'weeks');
          break;
        case MonthWeekDayDiv.Month:
          date = date.add(applyNum, 'M');
          break;
      }
    }

    return date;
  }

}



// WEBPACK FOOTER //
// ./src/app/feature/contact-alert-setting/contact-alert-setting-modal.component.ts